/**
 * Who is signed in, for the whole app.
 *
 * ── `status` has four values, and the first one is the reason this exists ───
 * `checking` is not `signed-out`. On every page load there is a round trip
 * before the answer is known, and an app that treats "not yet known" as "not
 * signed in" flashes the login screen at somebody who is signed in, every
 * single refresh. That flash is the defect this context is shaped to prevent,
 * so nothing outside may collapse the two.
 *
 *   checking     the session probe is in flight. Render the quiet boot ground.
 *   signed-in    `person` is set.
 *   signed-out   there is no session. The login screen is correct.
 *   unreachable  RocketRep's own server did not answer. NOT the same as being
 *                signed out — telling a rep to sign in again when the server is
 *                down sends them round a loop they cannot win, so it says so
 *                and offers a retry instead.
 *
 * ── No token, anywhere ─────────────────────────────────────────────────────
 * The session is an httpOnly cookie. This context holds a `Person` and nothing
 * else; there is no token to hold. It also holds no LaaS anything: RocketRep
 * owns its own accounts (Edward, 2026-09-16).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import {
  SESSION_LOST,
  fetchSession,
  postChangePassword,
  postLogin,
  postLogout,
} from "./api";
import type { ChangeOutcome, LoginOutcome } from "./api";
import type { Person } from "./types";

export type AuthStatus = "checking" | "signed-in" | "signed-out" | "unreachable";

export interface AuthValue {
  status: AuthStatus;
  person: Person | null;
  /**
   * True when the password in use was issued rather than chosen. The guards
   * divert to the change-password screen while it is set; everything else in
   * the product stays shut, because a password somebody else knows is not a
   * password.
   */
  mustChangePassword: boolean;
  /** Why the server could not be reached, when `status === "unreachable"`. */
  unreachableReason: string | null;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<ChangeOutcome>;
  /** Re-run the session probe — the retry on the unreachable panel. */
  recheck: () => void;
}

/** Distinguishes "no provider above me" from "provider, no session". */
const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("checking");
  const [person, setPerson] = useState<Person | null>(null);
  const [mustChange, setMustChange] = useState(false);
  const [unreachableReason, setUnreachableReason] = useState<string | null>(null);
  const [probe, setProbe] = useState(0);

  // StrictMode mounts effects twice in development. The abort keeps the second
  // run from resolving into state the first one already owns.
  const live = useRef(true);

  useEffect(() => {
    live.current = true;
    const ac = new AbortController();
    let cancelled = false;

    // A repeat probe (the retry button) must show that something is happening.
    setStatus((s) => (s === "checking" ? s : "checking"));

    void fetchSession(ac.signal).then((out) => {
      if (cancelled || !live.current) return;
      if (out.kind === "signed-in") {
        setPerson(out.person);
        setMustChange(out.person.mustChangePassword === true);
        setUnreachableReason(null);
        setStatus("signed-in");
      } else if (out.kind === "unreachable") {
        setPerson(null);
        setMustChange(false);
        setUnreachableReason(out.message);
        setStatus("unreachable");
      } else {
        setPerson(null);
        setMustChange(false);
        setUnreachableReason(null);
        setStatus("signed-out");
      }
    });

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [probe]);

  // A session that expires while the app is open. Without this the next screen
  // simply stops loading and nobody is told why.
  useEffect(() => {
    const onLost = () => {
      setPerson(null);
      setMustChange(false);
      setUnreachableReason(null);
      setStatus("signed-out");
    };
    window.addEventListener(SESSION_LOST, onLost);
    return () => window.removeEventListener(SESSION_LOST, onLost);
  }, []);

  useEffect(
    () => () => {
      live.current = false;
    },
    [],
  );

  const login = useCallback(async (email: string, password: string) => {
    const out = await postLogin(email, password);
    if (out.kind === "ok") {
      setPerson(out.person);
      setMustChange(false);
      setUnreachableReason(null);
      setStatus("signed-in");
    } else if (out.kind === "must-change-password") {
      // The server has issued a session — it just will not let them do anything
      // else until the password is theirs. If it sent no person with the 428,
      // the change screen still works: it only needs the cookie.
      if (out.person) setPerson(out.person);
      setMustChange(true);
      setUnreachableReason(null);
      setStatus("signed-in");
    }
    return out;
  }, []);

  const logout = useCallback(async () => {
    await postLogout();
    setPerson(null);
    setMustChange(false);
    setUnreachableReason(null);
    setStatus("signed-out");
  }, []);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      const out = await postChangePassword(currentPassword, newPassword);
      if (out.kind === "ok") {
        setMustChange(false);
        if (out.person) setPerson(out.person);
        // A server that rotates the session on a password change is doing the
        // right thing. Re-probe so we are holding whatever it left us with,
        // rather than assuming the old cookie survived.
        setProbe((n) => n + 1);
      } else if (out.kind === "signed-out") {
        setPerson(null);
        setMustChange(false);
        setStatus("signed-out");
      }
      return out;
    },
    [],
  );

  const recheck = useCallback(() => setProbe((n) => n + 1), []);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      person,
      mustChangePassword: mustChange,
      unreachableReason,
      login,
      logout,
      changePassword,
      recheck,
    }),
    [status, person, mustChange, unreachableReason, login, logout, changePassword, recheck],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Inside the app. Throws if somebody mounts a guarded screen with no provider. */
export function useAuth(): AuthValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error("useAuth() outside <AuthProvider>");
  return v;
}

/**
 * For components that must also render outside the app — the PRO screens are
 * dropped into a screenshot harness with no provider in the tree, and a throw
 * there would break a tool that has nothing to do with authentication.
 */
export function useOptionalAuth(): AuthValue | null {
  return useContext(AuthContext);
}
